import React from "react"
import { Table, Tag, Dropdown } from "antd"
import { MoreOutlined } from "@ant-design/icons"
import { translations } from "../../utils/Translation/staticTranslation"
import CampaignFilters from "./CampaignFilters"
import "./Campaigns.css"

const CampaignsTableWithFilters = ({
  data,
  loading = false,
  searchValue = "",
  statusFilter = "All Status",
  typeFilter = "All Types",
  pagination = { current: 1, pageSize: 10, total: 0 },
  onSearchChange,
  onStatusFilterChange,
  onTypeFilterChange,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onCreateCampaign,
  readOnly = false,
  canDelete = true,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.campaignsPage
  const isRTL = lang === "ar"

  const getStatusTag = (status) => {
    const normalizedStatus = (status || "").toLowerCase()

    const statusMap = {
      completed: "Completed",
      saved: "Saved",
      scheduled: "Scheduled",
      draft: "Draft",
      send_immediately: "Send Immediately",
      "send immediately": "Send Immediately",
      canceled: "Canceled",
      cancelled: "Canceled",
      failed: "Failed",
      queue: "Queue",
    }

    const displayStatus = statusMap[normalizedStatus] || status || "Draft"

    // Get translated status text
    const getTranslatedStatus = (statusKey) => {
      const statusTranslations = {
        Completed: t?.filters?.status?.completed || "Completed",
        Saved: t?.filters?.status?.saved || "Saved",
        Scheduled: t?.filters?.status?.scheduled || "Scheduled",
        Draft: t?.filters?.status?.draft || "Draft",
        "Send Immediately":
          t?.filters?.status?.send_immediately || "Send Immediately",
        Canceled: t?.filters?.status?.canceled || "Canceled",
        Failed: t?.filters?.status?.failed || "Failed",
        Queue: t?.filters?.status?.queue || "Queue",
      }
      return statusTranslations[statusKey] || statusKey
    }

    const statusConfig = {
      Completed: { color: "#027A48", bg: "#ECFDF3", border: "#ABEFC6" },
      Saved: { color: "#155EEF", bg: "#EFF8FF", border: "#B2DDFF" },
      Scheduled: { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89" },
      Draft: { color: "#475467", bg: "#F2F4F7", border: "#E4E7EC" },
      "Send Immediately": {
        color: "#6941C6",
        bg: "#F4F3FF",
        border: "#D6D1FF",
      },
      Canceled: { color: "#667085", bg: "#F2F4F7", border: "#E4E7EC" },
      Failed: { color: "#D92D20", bg: "#FEF3F2", border: "#FECACA" },
      Queue: { color: "#B54708", bg: "#FFFAEB", border: "#FEDF89" },
    }

    const config = statusConfig[displayStatus] || statusConfig.Draft

    return (
      <Tag
        className="campaign-status-tag"
        style={{
          color: config.color,
          backgroundColor: config.bg,
          border: `1px solid ${config.border}`,
        }}
      >
        {getTranslatedStatus(displayStatus)}
      </Tag>
    )
  }

  const getTypeTag = (type) => {
    // Get translated type text
    const getTranslatedType = (typeKey) => {
      const typeTranslations = {
        Promotional: t?.filters?.types?.promotional || "Promotional",
        Seasonal: t?.filters?.types?.seasonal || "Seasonal",
        Welcome: t?.filters?.types?.welcome || "Welcome",
        Updates: t?.filters?.types?.updates || "Updates",
      }
      return typeTranslations[typeKey] || typeKey
    }

    return <Tag className="campaign-type-tag">{getTranslatedType(type)}</Tag>
  }

  const getScheduledOnDisplay = (
    scheduledOnDate,
    scheduledOnTime,
    status,
    scheduledOnTitle,
    sendImmediately
  ) => {
    const formatDateTime = (date, time) => {
      if (!date) return ""

      // Format date (already in YYYY-MM-DD format)
      const formattedDate = date

      // Format time - handle both "HH:mm:ss" and "HH:mm:ss.milliseconds" formats
      let formattedTime = ""
      if (time) {
        // Remove milliseconds if present (e.g., "12:48:00" or "10:59:34.162978")
        const timeParts = time.split(".")
        const timeWithoutMs = timeParts[0] // Get "HH:mm:ss" part
        const timeOnly = timeWithoutMs.substring(0, 5) // Get "HH:mm" part
        formattedTime = timeOnly
      }

      // Combine date and time
      if (formattedDate && formattedTime) {
        return `${formattedDate} ${formattedTime}`
      } else if (formattedDate) {
        return formattedDate
      }
      return ""
    }

    const dateTime = formatDateTime(scheduledOnDate, scheduledOnTime)

    let displayTitle = scheduledOnTitle
    let titleColor = "#DC6803"

    if (!displayTitle) {
      if (sendImmediately || status === "Completed" || status === "مكتمل") {
        displayTitle =
          t?.filters?.status?.send_immediately ||
          "Send Immediately" ||
          "إرسال فوري"
        titleColor = "#6941C6"
      } else if (status === "Scheduled" || status === "Saved") {
        displayTitle = t?.filters?.status?.scheduled || "Scheduled"
        titleColor = "#DC6803"
      } else {
        displayTitle =
          t?.filters?.status?.send_immediately ||
          "Send Immediately" ||
          "إرسال فوري"
        titleColor = "#6941C6"
      }
    } else {
      // Translate displayTitle if it matches known statuses
      const titleTranslations = {
        "Send Immediately":
          t?.filters?.status?.send_immediately || "Send Immediately",
        Scheduled: t?.filters?.status?.scheduled || "Scheduled",
        Saved: t?.filters?.status?.saved || "Saved",
        Draft: t?.filters?.status?.draft || "Draft",
        Completed: t?.filters?.status?.completed || "Completed",
        Canceled: t?.filters?.status?.canceled || "Canceled",
        Failed: t?.filters?.status?.failed || "Failed",
      }

      // Check if original title is "Send Immediately" before translation
      const isSendImmediately =
        scheduledOnTitle === "Send Immediately" ||
        sendImmediately ||
        scheduledOnTitle === "إرسال فوري"

      displayTitle = titleTranslations[displayTitle] || displayTitle

      // Check for blue color - handle both English and Arabic
      if (
        isSendImmediately ||
        displayTitle.toLowerCase().includes("immediately") ||
        displayTitle.toLowerCase().includes("send") ||
        displayTitle.includes("إرسال فوري")
      ) {
        titleColor = "#6941C6"
      } else {
        titleColor = "#DC6803"
      }
    }

    return (
      <div>
        <div className="campaign-scheduled-title" style={{ color: titleColor }}>
          {displayTitle}
        </div>
        {dateTime && (
          <div className="campaign-scheduled-datetime">{dateTime}</div>
        )}
      </div>
    )
  }

  const columns = [
    {
      title: t?.table?.campaign || "Campaign",
      dataIndex: "campaign",
      key: "campaign",
      width: 220,
      render: (text, record) => (
        <div>
          <div className="campaign-name-cell">{text}</div>
          <div className="campaign-creator-cell">
            {t?.table?.by || "by"} {record.creator}
          </div>
        </div>
      ),
    },
    {
      title: t?.table?.description || "Description",
      dataIndex: "description",
      key: "description",
      width: 150,
      render: (text) => {
        if (!text || text === "")
          return <div className="campaign-description-cell">—</div>
        const textStr = String(text)
        const truncatedText =
          textStr.length > 50 ? textStr.substring(0, 50) + "..." : textStr
        return (
          <div className="campaign-description-cell" title={textStr}>
            {truncatedText}
          </div>
        )
      },
    },
    {
      title: t?.table?.type || "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type) => getTypeTag(type),
    },
    {
      title: t?.table?.status || "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: t?.table?.audience || "Audience",
      dataIndex: "audience",
      key: "audience",
      width: 150,
      render: (text) => <div className="campaign-description-cell">{text}</div>,
    },
    {
      title: t?.table?.scheduled_on || "Scheduled On",
      dataIndex: "scheduledOn",
      key: "scheduledOn",
      width: 180,
      render: (_, record) =>
        getScheduledOnDisplay(
          record.scheduledOnDate,
          record.scheduledOnTime,
          record.status,
          record.scheduledOnTitle,
          record.sendImmediately
        ),
    },
    {
      title: t?.table?.created_on || "Created On",
      dataIndex: "createdOn",
      key: "createdOn",
      width: 120,
      render: (date) => {
        const formatDate = (dateString) => {
          if (!dateString) return ""
          const date = new Date(dateString)
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, "0")
          const day = String(date.getDate()).padStart(2, "0")
          return `${year}-${month}-${day}`
        }
        return (
          <div className="campaign-created-on-cell">{formatDate(date)}</div>
        )
      },
    },
    {
      title: t?.table?.actions || "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => {
        const menuItems = [
          {
            key: "view",
            label: (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <img
                  src="/Campaign/arrow-up-right.svg"
                  alt="View"
                  style={{ width: "16px", height: "16px" }}
                />
                {t?.buttons?.view || "View"}
              </span>
            ),
            onClick: () => onView && onView(record),
          },
        ]

        const normalizedStatus = (record.status || "").toLowerCase()
        const isQueueOrCompleted =
          normalizedStatus === "queue" ||
          normalizedStatus === "completed" ||
          record.status === "Queue" ||
          record.status === "Completed"

        if (!isQueueOrCompleted) {
          menuItems.push({
            key: "edit",
            disabled: readOnly,
            label: (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <img
                  src="/Campaign/Button-edit.svg"
                  alt="Edit"
                  style={{ width: "16px", height: "16px" }}
                />
                {t?.buttons?.edit || "Edit"}
              </span>
            ),
            onClick: () => !readOnly && onEdit && onEdit(record),
          })
          if (canDelete) {
            menuItems.push({
              key: "delete",
              disabled: readOnly,
              label: (
                <span
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <img
                    src="/Campaign/delete-icon.svg"
                    alt="Delete"
                    style={{ width: "16px", height: "16px" }}
                  />
                  {t?.buttons?.delete || "Delete"}
                </span>
              ),
              onClick: () => !readOnly && onDelete && onDelete(record),
            })
          }
        }

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <MoreOutlined className="campaign-actions-icon" />
          </Dropdown>
        )
      },
    },
  ]

  const handleTableChange = (paginationInfo) => {
    if (onPageChange) {
      onPageChange(paginationInfo.current)
    }
  }

  return (
    <div className="campaigns-table-container campaigns-table-wrapper">
      <CampaignFilters
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        typeFilter={typeFilter}
        onTypeFilterChange={onTypeFilterChange}
        onCreateCampaign={onCreateCampaign}
        readOnly={readOnly}
      />
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          current: pagination.current,
          total: pagination.total,
          pageSize: pagination.pageSize,
          showSizeChanger: false,
          showLessItems: false,
          position: ["bottomRight"],
          style: {
            marginTop: "16px",
            marginBottom: "16px",
            textAlign: "right",
          },
          itemRender: (page, type, originalElement) => {
            const totalPages = Math.ceil(pagination.total / pagination.pageSize)

            if (type === "prev") {
              return (
                <a
                  onClick={(e) => {
                    if (isRTL) {
                      // In RTL, "prev" button is on the right and should go to next page
                      if (pagination.current < totalPages) {
                        e.preventDefault()
                        handleTableChange({ current: pagination.current + 1 })
                      }
                    } else {
                      // In LTR, "prev" button goes to previous page
                      if (pagination.current > 1) {
                        e.preventDefault()
                        handleTableChange({ current: pagination.current - 1 })
                      }
                    }
                  }}
                  style={{
                    color: isRTL
                      ? pagination.current >= totalPages
                        ? "#D0D5DD"
                        : "#18181B"
                      : pagination.current === 1
                      ? "#D0D5DD"
                      : "#18181B",
                    fontSize: "12px",
                    fontWeight: 400,
                    textDecoration: "none",
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: isRTL
                      ? pagination.current >= totalPages
                        ? "not-allowed"
                        : "pointer"
                      : pagination.current === 1
                      ? "not-allowed"
                      : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    pointerEvents: isRTL
                      ? pagination.current >= totalPages
                        ? "none"
                        : "auto"
                      : pagination.current === 1
                      ? "none"
                      : "auto",
                  }}
                >
                  {isRTL ? (
                    <>
                      {t?.audience?.previous || "Previous"}
                      <img
                        src="/Campaign/ChevronRight.svg"
                        alt="Previous"
                        style={{
                          width: "16px",
                          height: "16px",
                          transform: "scaleX(-1)",
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <img
                        src="/Campaign/ChevronLeft.svg"
                        alt="Previous"
                        style={{ width: "16px", height: "16px" }}
                      />
                      {t?.audience?.previous || "Previous"}
                    </>
                  )}
                </a>
              )
            }
            if (type === "next") {
              return (
                <a
                  onClick={(e) => {
                    if (isRTL) {
                      // In RTL, "next" button is on the left and should go to previous page
                      if (pagination.current > 1) {
                        e.preventDefault()
                        handleTableChange({ current: pagination.current - 1 })
                      }
                    } else {
                      // In LTR, "next" button goes to next page
                      if (pagination.current < totalPages) {
                        e.preventDefault()
                        handleTableChange({ current: pagination.current + 1 })
                      }
                    }
                  }}
                  style={{
                    color: isRTL
                      ? pagination.current === 1
                        ? "#D0D5DD"
                        : "#18181B"
                      : pagination.current >= totalPages
                      ? "#D0D5DD"
                      : "#18181B",
                    fontSize: "12px",
                    fontWeight: 400,
                    textDecoration: "none",
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: isRTL
                      ? pagination.current === 1
                        ? "not-allowed"
                        : "pointer"
                      : pagination.current >= totalPages
                      ? "not-allowed"
                      : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    pointerEvents: isRTL
                      ? pagination.current === 1
                        ? "none"
                        : "auto"
                      : pagination.current >= totalPages
                      ? "none"
                      : "auto",
                  }}
                >
                  {isRTL ? (
                    <>
                      <img
                        src="/Campaign/ChevronLeft.svg"
                        alt="Next"
                        style={{
                          width: "16px",
                          height: "16px",
                          transform: "scaleX(-1)",
                        }}
                      />
                      {t?.audience?.next || "Next"}
                    </>
                  ) : (
                    <>
                      {t?.audience?.next || "Next"}
                      <img
                        src="/Campaign/ChevronRight.svg"
                        alt="Next"
                        style={{ width: "16px", height: "16px" }}
                      />
                    </>
                  )}
                </a>
              )
            }
            if (type === "page") {
              const isActive = page === pagination.current
              return (
                <a
                  onClick={(e) => {
                    e.preventDefault()
                    handleTableChange({ current: page })
                  }}
                  style={{
                    color: "#18181B",
                    backgroundColor: isActive ? "#F9FAFB" : "transparent",
                    padding: isActive ? "8px 14px" : "8px 14px",
                    borderRadius: isActive ? "8px" : "8px",
                    fontSize: "14px",
                    fontWeight: 400,
                    border: isActive
                      ? "1px solid #D0D5DD"
                      : "1px solid transparent",
                    textDecoration: "none",
                    display: "inline-block",
                    textAlign: "center",
                    cursor: "pointer",
                    minWidth: "36px",
                  }}
                >
                  {page}
                </a>
              )
            }
            if (type === "jump-prev" || type === "jump-next") {
              // Calculate target page for ellipsis click
              let targetPage
              if (type === "jump-prev") {
                targetPage = Math.max(1, pagination.current - 5)
              } else {
                targetPage = Math.min(totalPages, pagination.current + 5)
              }

              return (
                <a
                  onClick={(e) => {
                    e.preventDefault()
                    handleTableChange({ current: targetPage })
                  }}
                  style={{
                    color: "#18181B",
                    fontSize: "14px",
                    border: "none",
                    background: "transparent",
                    padding: "8px 14px",
                    cursor: "pointer",
                    display: "inline-block",
                    textAlign: "center",
                    minWidth: "36px",
                  }}
                >
                  ...
                </a>
              )
            }
            return originalElement
          },
          onChange: (page, pageSize) => {
            handleTableChange({ current: page, pageSize })
          },
        }}
        rowKey="id"
      />
    </div>
  )
}

export default CampaignsTableWithFilters
